import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Avatar, 
  Upload, 
  Switch, 
  Select, 
  Divider,
  Space,
  Tabs,
  Statistic,
  Tag,
  Alert,
  Modal,
  message,
  Spin,
  List,
  Table
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { 
  UserService, 
  UserProfileDetails, 
  UserProfileUpdateRequest,
  UserProfileStats
} from '../../shared/api';
import { PaymentAPI } from '../../shared/api/conversationService';
import {
  UserOutlined,
  EditOutlined,
  CameraOutlined,
  SaveOutlined,
  SettingOutlined,
  BellOutlined,
  GlobalOutlined,
  SecurityScanOutlined,
  CreditCardOutlined,
  BarChartOutlined,
  TrophyOutlined,
  CalendarOutlined,
  CrownOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { chatGPTTheme } from '../../assets/styles/theme';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

// 用户数据类型
interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  bio: string;
  location: string;
  language: string;
  timezone: string;
  joinDate: string;
}

interface UserStats {
  totalConsultations: number;
  completedCases: number;
  favoriteTopics: string[];
  lastActiveDate: string;
  lastPaymentDate: string;
  paymentStatus: string;
}

// 支付记录接口
interface PaymentRecord {
  id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  status: string;
  description: string;
}

// 在接口定义部分添加订阅状态接口
interface SubscriptionInfo {
  id: number;
  plan_id: string;
  plan_name: string;
  plan_type: string;
  status: string;
  is_recurring: boolean;
  remaining_conversations: number;
  total_conversations_granted: number;
  conversations_used_this_period: number;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  stripe_subscription_id?: string;
}

interface UserSubscriptionStatus {
  active_subscriptions: SubscriptionInfo[];
  has_active_subscription: boolean;
  total_remaining_conversations: number;
  next_billing_date?: string;
}

// Styled Components
const ProfileContainer = styled.div`
  min-height: 100vh;
  background: ${chatGPTTheme.colors.background};
  padding: ${chatGPTTheme.spacing.xl};
  
  @media (max-width: 768px) {
    padding: ${chatGPTTheme.spacing.md} ${chatGPTTheme.spacing.sm};
  }
`;

const HeaderCard = styled(Card)`
  &.ant-card {
    margin-bottom: ${chatGPTTheme.spacing.lg};
    border-radius: ${chatGPTTheme.borderRadius.xl};
    background: linear-gradient(135deg, ${chatGPTTheme.colors.primary}15, ${chatGPTTheme.colors.secondary}15);
    border: 1px solid ${chatGPTTheme.colors.border};
    
    @media (max-width: 768px) {
      margin-bottom: ${chatGPTTheme.spacing.md};
      border-radius: ${chatGPTTheme.borderRadius.lg};
      
      .ant-card-body {
        padding: 16px 12px;
      }
    }
  }
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  @media (max-width: 768px) {
    margin-bottom: ${chatGPTTheme.spacing.md};
  }
  
  .avatar-upload {
    position: relative;
    margin-bottom: ${chatGPTTheme.spacing.md};
    
    @media (max-width: 768px) {
      margin-bottom: ${chatGPTTheme.spacing.sm};
    }
    
    .ant-upload {
      display: block;
    }
    
    &:hover .avatar-overlay {
      opacity: 1;
    }
  }
  
  .avatar-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    cursor: pointer;
    
    .anticon {
      color: white;
      font-size: 1.5rem;
      
      @media (max-width: 768px) {
        font-size: 1.2rem;
      }
    }
  }
  
  .ant-typography {
    @media (max-width: 768px) {
      font-size: 16px !important;
      margin-bottom: 4px !important;
    }
  }
`;

const StatsCard = styled(Card)`
  &.ant-card {
    text-align: center;
    border-radius: ${chatGPTTheme.borderRadius.lg};
    transition: all 0.3s ease;
    
    @media (max-width: 768px) {
      border-radius: ${chatGPTTheme.borderRadius.md};
      margin-bottom: ${chatGPTTheme.spacing.sm};
      
      .ant-card-body {
        padding: 12px 8px;
      }
      
      .ant-statistic-title {
        font-size: 12px !important;
        margin-bottom: 4px !important;
      }
      
      .ant-statistic-content {
        font-size: 18px !important;
      }
    }
    
    &:hover {
      box-shadow: ${chatGPTTheme.shadows.md};
      border-color: ${chatGPTTheme.colors.primary};
    }
    
    .ant-statistic-content {
      color: ${chatGPTTheme.colors.primary};
    }
  }
`;

const SettingCard = styled(Card)`
  &.ant-card {
    margin-bottom: ${chatGPTTheme.spacing.lg};
    border-radius: ${chatGPTTheme.borderRadius.lg};
    
    @media (max-width: 768px) {
      margin-bottom: ${chatGPTTheme.spacing.md};
      border-radius: ${chatGPTTheme.borderRadius.md};
      
      .ant-card-body {
        padding: 16px 12px;
      }
      
      .ant-card-head {
        padding: 0 12px;
        min-height: 48px;
        
        .ant-card-head-title {
          font-size: 16px;
        }
      }
    }
    
    .ant-card-head {
      border-bottom: 1px solid ${chatGPTTheme.colors.border};
    }
  }
`;

const MobileTabs = styled(Tabs)`
  @media (max-width: 768px) {
    .ant-tabs-nav {
      margin-bottom: 16px;
      
      .ant-tabs-nav-wrap {
        .ant-tabs-nav-list {
          .ant-tabs-tab {
            padding: 8px 12px;
            font-size: 14px;
            margin: 0 4px;
            
            .ant-tabs-tab-btn {
              font-size: 14px;
            }
            
            .anticon {
              font-size: 14px;
              margin-right: 4px;
            }
          }
        }
      }
    }
    
    .ant-tabs-content-holder {
      .ant-tabs-content {
        .ant-tabs-tabpane {
          padding: 0;
        }
      }
    }
  }
`;

const MobileFormCard = styled(Card)`
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 16px 12px;
    }
    
    .ant-form-item {
      margin-bottom: 16px;
      
      .ant-form-item-label {
        padding-bottom: 4px;
        
        label {
          font-size: 14px;
        }
      }
      
      .ant-form-item-control {
        .ant-input, .ant-input-disabled, .ant-select-selector {
          font-size: 14px;
          padding: 8px 12px;
        }
        
        .ant-input {
          border-radius: 6px;
        }
        
        textarea.ant-input {
          font-size: 14px;
        }
      }
    }
    
    .ant-btn {
      font-size: 14px;
      padding: 6px 12px;
      height: auto;
      border-radius: 6px;
    }
  }
`;

const MobileTableContainer = styled.div`
  @media (max-width: 768px) {
    .ant-table-wrapper {
      .ant-table {
        font-size: 12px;
        
        .ant-table-thead > tr > th {
          padding: 8px 4px;
          font-size: 12px;
          font-weight: 500;
        }
        
        .ant-table-tbody > tr > td {
          padding: 8px 4px;
          font-size: 12px;
        }
        
        .ant-tag {
          font-size: 11px;
          padding: 2px 6px;
          margin: 0;
        }
      }
      
      .ant-pagination {
        .ant-pagination-item {
          min-width: 28px;
          height: 28px;
          line-height: 26px;
        }
        
        .ant-pagination-prev, .ant-pagination-next {
          min-width: 28px;
          height: 28px;
          line-height: 26px;
        }
      }
    }
  }
`;

const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profileDetails, setProfileDetails] = useState<UserProfileDetails | null>(null);
  const [userStats, setUserStats] = useState<UserProfileStats | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyReport: true,
    caseUpdates: true
  });
  const [paymentRecords, setPaymentRecords] = useState<PaymentRecord[]>([]);
  // 添加订阅状态管理
  const [subscriptionStatus, setSubscriptionStatus] = useState<UserSubscriptionStatus | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 获取用户详细资料
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const [details, stats] = await Promise.all([
        UserService.getUserProfileDetails(),
        UserService.getUserProfileStats()
      ]);
      setProfileDetails(details);
      setUserStats(stats);
      form.setFieldsValue(details);
    } catch (error) {
      message.error(t('profile.basic.updateFailed'));
      console.error('获取用户资料失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取支付记录
  const fetchPaymentRecords = async () => {
    try {
      // 使用会话服务统一的 BASE 地址，避免硬编码 localhost
      const CONVERSATION_API_BASE = process.env.REACT_APP_CONVERSATION_API_BASE || 'http://localhost:9002';
      const response = await fetch(`${CONVERSATION_API_BASE}/api/payments/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (!response.ok) {
        throw new Error(t('profile.paymentHistory.fetchFailed'));
      }
      const data = await response.json();
      // console.log('🔍 API返回的支付历史数据:', data);
      
      // 转换数据结构以匹配前端需求 - 添加空值检查
      const formattedRecords = data
        .filter((payment: any) => payment && payment.payment_id) // 过滤掉空记录
        .map((payment: any, index: number) => ({
          id: payment.payment_id ? payment.payment_id.toString() : `payment-${index}`,
          amount: payment.amount_jpy || 0,
          payment_date: payment.paid_at || payment.created_at,
          payment_method: payment.payment_method ? payment.payment_method.toLowerCase() : 'unknown',
          status: payment.status ? payment.status.toLowerCase() : 'unknown',
          description: payment.description || payment.plan_type || '法律相談'
        }));
      // console.log('🔍 格式化后的支付记录:', formattedRecords);
      setPaymentRecords(formattedRecords);
    } catch (error) {
      console.error('支払い履歴の取得エラー:', error);
      message.error(t('profile.paymentHistory.fetchFailed'));
    }
  };

  // 添加获取订阅状态的函数
  const fetchSubscriptionStatus = async () => {
    try {
      setSubscriptionLoading(true);
      const status = await PaymentAPI.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('获取订阅状态失败:', error);
      message.error('获取订阅状态失败');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // 添加取消订阅的函数
  const handleCancelSubscription = async (subscriptionId: string, cancelAtPeriodEnd: boolean = true) => {
    Modal.confirm({
      title: cancelAtPeriodEnd ? t('profile.subscription.confirmCancel') : t('profile.subscription.confirmCancelImmediate'),
      content: cancelAtPeriodEnd 
        ? t('profile.subscription.cancelDescription')
        : t('profile.subscription.cancelImmediateDescription'),
      okText: t('profile.subscription.confirmCancelButton'),
      cancelText: t('profile.subscription.keepSubscription'),
      okType: 'danger',
      onOk: async () => {
        try {
          setSubscriptionLoading(true);
          await PaymentAPI.cancelSubscription(subscriptionId, { cancel_at_period_end: cancelAtPeriodEnd });
          message.success(cancelAtPeriodEnd ? t('profile.subscription.cancelScheduled') : t('profile.subscription.canceledImmediately'));
          await fetchSubscriptionStatus(); // 刷新订阅状态
        } catch (error) {
          console.error('取消订阅失败:', error);
          message.error(t('profile.subscription.cancelFailed'));
        } finally {
          setSubscriptionLoading(false);
        }
      }
    });
  };

  // 更新useEffect以获取订阅状态
  useEffect(() => {
    fetchUserProfile();
    fetchPaymentRecords();
    fetchSubscriptionStatus(); // 添加这行
  }, []);

  // 在获取到用户信息后再获取支付记录
  useEffect(() => {
    if (profileDetails?.id) {
      fetchPaymentRecords();
    }
  }, [profileDetails?.id]);

  // 处理表单提交
  const handleSubmit = async (values: UserProfileUpdateRequest) => {
    try {
      setLoading(true);
      const updatedProfile = await UserService.updateUserProfileDetails(values);
      setProfileDetails(updatedProfile);
      message.success(t('profile.basic.updateSuccess'));
      setEditMode(false);
    } catch (error) {
      message.error(t('profile.basic.updateFailed'));
      console.error('更新失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理头像上传
  const handleAvatarChange = async (info: any) => {
    if (info.file.status === 'uploading') {
      setAvatarLoading(true);
      return;
    }

    if (info.file.status === 'done') {
      try {
        const result = await UserService.uploadAvatar(info.file.originFileObj);
        setProfileDetails(prev => prev ? { ...prev, avatar: result.avatar_url } : null);
        message.success(t('profile.basic.avatarUpdateSuccess'));
      } catch (error) {
        message.error(t('profile.basic.avatarUpdateFailed'));
        console.error('头像上传失败:', error);
      } finally {
        setAvatarLoading(false);
      }
    }
  };

  // 处理通知设置更改
  const handleNotificationChange = (key: string, value: boolean) => {
    setNotifications(prev => ({ ...prev, [key]: value }));
    message.success(t('profile.notifications.updateSuccess'));
  };

  // 处理账户删除
  const handleDeleteAccount = () => {
    Modal.confirm({
      title: t('profile.account.deleteConfirmTitle'),
      content: t('profile.account.deleteConfirmContent'),
      okText: t('profile.account.delete'),
      okType: 'danger',
      cancelText: t('profile.basic.cancel'),
      onOk: () => {
        message.success(t('profile.account.deleteRequestSuccess'));
      }
    });
  };

  // 渲染基本信息表单
  const renderBasicInfoForm = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={profileDetails || {}}
    >
      <Row gutter={[24, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="name"
            label={t('profile.basic.name')}
            rules={[{ required: true, message: t('profile.basic.nameRequired') }]}
          >
            <Input disabled={!editMode} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label={t('profile.basic.email')}
          >
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="phone"
            label={t('profile.basic.phone')}
          >
            <Input disabled={!editMode} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="location"
            label={t('profile.basic.location')}
          >
            <Input disabled={!editMode} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name="bio"
        label={t('profile.basic.bio')}
      >
        <Input.TextArea rows={4} disabled={!editMode} />
      </Form.Item>
      <Form.Item>
        {editMode ? (
          <Space wrap>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              {t('profile.basic.save')}
            </Button>
            <Button onClick={() => setEditMode(false)}>
              {t('profile.basic.cancel')}
            </Button>
          </Space>
        ) : (
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => setEditMode(true)}
          >
            {t('profile.basic.edit')}
          </Button>
        )}
      </Form.Item>
    </Form>
  );

  // 渲染用户统计信息
  const renderUserStats = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={8}>
        <StatsCard>
          <Statistic
            title={t('profile.stats.totalConsultations')}
            value={userStats?.total_conversations || 0}
            prefix={<BarChartOutlined />}
          />
        </StatsCard>
      </Col>
      <Col xs={24} sm={8}>
        <StatsCard>
          <Statistic
            title={t('profile.stats.paymentCount')}
            value={paymentRecords.length}
            prefix={<CreditCardOutlined />}
          />
        </StatsCard>
      </Col>
      <Col xs={24} sm={8}>
        <StatsCard>
          <Statistic
            title={t('profile.stats.subscriptionStatus')}
            value={subscriptionStatus?.has_active_subscription ? t('profile.stats.active') : t('profile.stats.inactive')}
            valueStyle={{ 
              color: subscriptionStatus?.has_active_subscription ? '#3f8600' : '#8c8c8c',
              fontSize: isMobile ? '16px' : '1.2rem'
            }}
            prefix={subscriptionStatus?.has_active_subscription ? 
              <CrownOutlined style={{ color: '#3f8600' }} /> : 
              <UserOutlined style={{ color: '#8c8c8c' }} />
            }
          />
        </StatsCard>
      </Col>
    </Row>
  );

  // 渲染支付记录表格
  const renderPaymentHistory = () => {
    const getStatusColor = (status: string) => {
      switch (status.toLowerCase()) {
        case 'completed':
        case 'succeeded':
          return 'success';
        case 'pending':
          return 'processing';
        case 'failed':
        case 'cancelled':
          return 'error';
        default:
          return 'default';
      }
    };
    
    const getStatusText = (status: string) => {
      switch (status.toLowerCase()) {
        case 'completed':
        case 'succeeded':
          return t('profile.paymentHistory.completed');
        case 'pending':
          return t('profile.paymentHistory.pending');
        case 'failed':
          return t('profile.paymentHistory.failed');
        case 'cancelled':
          return t('profile.paymentHistory.cancelled');
        default:
          return status;
      }
    };

    const columns = [
      {
        title: t('profile.paymentHistory.paymentDate'),
        dataIndex: 'payment_date',
        key: 'payment_date',
        width: isMobile ? 80 : undefined,
        render: (date: string) => new Date(date).toLocaleDateString('ja-JP')
      },
      {
        title: t('profile.paymentHistory.amount'),
        dataIndex: 'amount',
        key: 'amount',
        width: isMobile ? 70 : undefined,
        render: (amount: number) => `¥${amount.toLocaleString()}`
      },
      ...(isMobile ? [] : [{
        title: t('profile.paymentHistory.paymentMethod'),
        dataIndex: 'payment_method',
        key: 'payment_method'
      }]),
      {
        title: t('profile.paymentHistory.status'),
        dataIndex: 'status',
        key: 'status',
        width: isMobile ? 60 : undefined,
        render: (status: string) => (
          <Tag color={getStatusColor(status)}>
            {getStatusText(status)}
          </Tag>
        )
      },
      ...(isMobile ? [] : [{
        title: t('profile.paymentHistory.description'),
        dataIndex: 'description',
        key: 'description'
      }])
    ];

    return (
      <MobileTableContainer>
        <Card title={t('profile.paymentHistory.title')}>
          <Table
            columns={columns}
            dataSource={paymentRecords}
            rowKey="id"
            scroll={{ x: isMobile ? 600 : undefined }}
            pagination={{
              pageSize: isMobile ? 5 : 10,
              showSizeChanger: false,
              size: isMobile ? 'small' : 'default'
            }}
          />
        </Card>
      </MobileTableContainer>
    );
  };

  // 添加渲染订阅管理的函数
  const renderSubscriptionManagement = () => {
    if (subscriptionLoading) {
      return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
    }

    if (!subscriptionStatus?.has_active_subscription) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <CreditCardOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
            <Title level={4}>{t('profile.subscription.noActiveSubscription')}</Title>
            <Paragraph type="secondary">
              {t('profile.subscription.subscriptionDescription')}
            </Paragraph>
            <Button 
              type="primary" 
              size="large"
              onClick={() => window.location.href = '/pricing'}
            >
              {t('profile.subscription.viewPlans')}
            </Button>
          </div>
        </Card>
      );
    }

    return (
      <div>
        {subscriptionStatus.active_subscriptions.map((subscription) => (
          <Card key={subscription.id} style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={18}>
                <Space size={isMobile ? "small" : "large"} wrap>
                  <Space>
                    <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
                      {subscription.plan_name}
                    </Title>
                    <Tag 
                      color={subscription.status === 'active' ? 'green' : 'orange'}
                    >
                      {subscription.status === 'active' ? t('profile.subscription.active') : subscription.status}
                    </Tag>
                  </Space>
                  
                  <Text>
                    {subscription.is_recurring ? t('profile.subscription.recurring') : t('profile.subscription.oneTime')} | {t('profile.subscription.remainingConsultations')}{subscription.remaining_conversations}回
                  </Text>
                  
                  {subscription.current_period_end && (
                    <Text>
                      {t('profile.subscription.nextBilling')}: {new Date(subscription.current_period_end).toLocaleDateString('ja-JP')}
                    </Text>
                  )}
                  
                  <Text>
                    {t('profile.subscription.usedThisPeriod')}: {subscription.conversations_used_this_period}/{subscription.total_conversations_granted}
                  </Text>
                  
                  <Text>
                    {t('profile.subscription.totalConsultations')}: {subscription.total_conversations_granted}回
                  </Text>
                  
                  <Text>
                    {t('profile.subscription.remainingConsultations')}: <span style={{ color: subscription.remaining_conversations > 0 ? '#3f8600' : '#cf1322' }}>
                      {subscription.remaining_conversations}回
                    </span>
                  </Text>
                  
                  <Text>
                    {t('profile.subscription.usageRate')}: {((subscription.conversations_used_this_period / subscription.total_conversations_granted) * 100).toFixed(1)}%
                  </Text>
                </Space>
                
                {subscription.cancel_at_period_end && (
                  <div style={{ marginTop: '8px' }}>
                    <Text type="warning">
                      {t('profile.subscription.cancelWarning')}
                    </Text>
                  </div>
                )}
              </Col>
              
              <Col xs={24} sm={6} style={{ textAlign: isMobile ? 'left' : 'right', marginTop: isMobile ? '8px' : '0' }}>
                {subscription.is_recurring && subscription.stripe_subscription_id && !subscription.cancel_at_period_end && (
                  <Space direction={isMobile ? "horizontal" : "vertical"} wrap>
                    <Button
                      onClick={() => handleCancelSubscription(subscription.stripe_subscription_id!, true)}
                      size="small"
                    >
                      {t('profile.subscription.cancelAtPeriodEnd')}
                    </Button>
                    <Button
                      danger
                      size="small"
                      onClick={() => handleCancelSubscription(subscription.stripe_subscription_id!, false)}
                    >
                      {t('profile.subscription.cancelImmediately')}
                    </Button>
                  </Space>
                )}
                {subscription.cancel_at_period_end && (
                  <Button
                    type="primary"
                    size="small"
                    onClick={async () => {
                      try {
                        // 这里需要实现恢复订阅的功能
                        message.info(t('profile.subscription.restoreInDevelopment'));
                      } catch (error) {
                        message.error(t('profile.subscription.operationFailed'));
                      }
                    }}
                  >
                    {t('profile.subscription.restoreSubscription')}
                  </Button>
                )}
              </Col>
            </Row>
          </Card>
        ))}
        
        {subscriptionStatus.next_billing_date && (
          <Alert
            message={t('profile.subscription.nextBilling')}
            description={t('profile.subscription.nextBillingNotice', { date: new Date(subscriptionStatus.next_billing_date).toLocaleDateString('ja-JP') })}
            type="info"
            style={{ marginTop: '16px' }}
          />
        )}
      </div>
    );
  };

  if (loading && !profileDetails) {
    return (
      <ProfileContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Spin size="large" />
        </div>
      </ProfileContainer>
    );
  }

  return (
    <Spin spinning={loading}>
      <ProfileContainer>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* 头部信息卡片 */}
          <HeaderCard>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={6}>
                <AvatarSection>
                  <div className="avatar-upload">
                    <Upload
                      name="avatar"
                      showUploadList={false}
                      onChange={handleAvatarChange}
                    >
                      <Avatar
                        size={isMobile ? 80 : 120}
                        icon={<UserOutlined />}
                        src={profileDetails?.avatar}
                      />
                      <div className="avatar-overlay">
                        <CameraOutlined />
                      </div>
                    </Upload>
                  </div>
                  <Title level={isMobile ? 5 : 4}>{profileDetails?.name}</Title>
                  <Text type="secondary">{profileDetails?.email}</Text>
                </AvatarSection>
              </Col>
              <Col xs={24} sm={18}>
                {renderUserStats()}
              </Col>
            </Row>
          </HeaderCard>

          {/* 详细设置选项卡 */}
          <MobileTabs defaultActiveKey="basic">
            <TabPane 
              tab={<span><UserOutlined />{t('profile.tabs.basicInfo')}</span>}
              key="basic"
            >
              <MobileFormCard>
                {renderBasicInfoForm()}
              </MobileFormCard>
            </TabPane>
            <TabPane 
              tab={<span><CreditCardOutlined />{t('profile.tabs.subscription')}</span>}
              key="subscription"
            >
              {renderSubscriptionManagement()}
            </TabPane>
            <TabPane 
              tab={<span><BarChartOutlined />{t('profile.tabs.paymentHistory')}</span>}
              key="payment_history"
            >
              {renderPaymentHistory()}
            </TabPane>
          </MobileTabs>
        </div>
      </ProfileContainer>
    </Spin>
  );
};

export default ProfilePage;
